<template>
  <product-accordions
    v-if="!productDetails?.hide"
    v-bind="forwarded"
  />
  <div v-else>
    <template v-for="({ data, id, label, layout = 'grid', expanded, sections }, i) in groupedProductDetails" :key="id">
      <vf-accordion
        v-if="showAllProductDetails || i === 0"
        :id
        :open="$feature.allowOpenedDetailsAccordionsOnPDP ? expanded : i === 0"
        :title="label"
        variant="aligned"
        @close="handleClose(label ?? id)"
        @expand="handleExpand(id, label ?? id)"
      >
        <div
          class="pb-6 pt-2"
          :class="sections?.length ? 'body-2 c-black' : 'text-sm c-grey-20'"
        >
          <component
            :is="componentTypes[resolveComponentType(id, layout, sections)]"
            :data
            :sections
            :title="label"
          />
        </div>
      </vf-accordion>
    </template>

    <vf-button
      v-if="!showAllProductDetails"
      class="mt-4"
      variant="tertiary"
      @click="handleShowAllProductDetails"
    >
      {{ $t.productDetails }}
    </vf-button>
  </div>
</template>

<script lang="ts" setup>
import type { Props } from '#commerce/components/product/accordions/Accordions.vue'
import type { ProductDetailWithSections } from '#types/product'
import ProductAccordions from '#brand/components/product/accordions/Accordions.vue'
import {
  LazyProductAccordionsBulletin,
  LazyProductAccordionsDetailCard,
  LazyProductAccordionsFeaturesExtended,
  LazyProductAccordionsGrid,
  LazyProductAccordionsKeyValue,
  LazyProductAccordionsModalGrid,
  LazyProductAccordionsScale
} from '#components'

const props = defineProps<Props>()
const forwarded = useForwardProps(props)

const { trackEvent } = useOptimizely()
const { $gtm } = useNuxtApp()

const productDetails = useOptimizelyFlag('product_details')

const groupedProductDetails = computed(() => props.productDetails as ProductDetailWithSections[])
const showAllProductDetails = ref(false)

const handleExpand = (id: string, label: string) => {
  trackEvent('open_product_details_accordion', { id })
  $gtm.push('pdpPage.onAccordionInteraction', label, true)
}

const handleClose = (label: string) => {
  $gtm.push('pdpPage.onAccordionInteraction', label, false)
}

const handleShowAllProductDetails = () => {
  trackEvent('show_all_product_details_accordions', {})
  showAllProductDetails.value = true
}

const componentTypes = {
  bulletin: LazyProductAccordionsBulletin,
  detailCard: LazyProductAccordionsDetailCard,
  featuresExtended: LazyProductAccordionsFeaturesExtended,
  grid: LazyProductAccordionsGrid,
  keyValue: LazyProductAccordionsKeyValue,
  modalGrid: LazyProductAccordionsModalGrid,
  scale: LazyProductAccordionsScale
}

type ComponentType = keyof typeof componentTypes
const isComponentType = (value?: string): value is ComponentType =>
  !!value && Object.hasOwn(componentTypes, value)

const resolveComponentType = (id: string, layout?: string, sections?: ProductDetailWithSections['sections']): ComponentType => {
  if (sections?.length && isComponentType(id)) return id
  return isComponentType(layout) ? layout : 'detailCard'
}
</script>
